import React, { ReactNode } from 'react'
import {
  Card,
  Checkbox,
  Fieldset,
  Heading,
  Layout,
  Paragraph,
  Toggle,
} from '@vwfs-bronson/bronson-react'
import get from 'lodash/get'
import { connect } from 'react-redux'
import { FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'
import { RootState } from '../../../services/redux'
import { FormField } from '../../../components/FormField'
import { getTlsValid } from '../../../services/redux/features/custom.redux'
import { getStorefrontData } from '../../../services/redux/features/storefront.redux'
import { isAudiOrVWFS } from '../../../services/common/utils'

type DataProtectionFormValues = {
  selectall?: boolean
  allowPhoneAds?: boolean
  allowMailAds?: boolean
  allowProfilesCreation?: boolean
  dataPrivacyConsentAcceptance?: boolean
  termsAgreeCheckAgree?: boolean
  [key: string]: unknown
}

type CheckboxItemProps = {
  label?: ReactNode
  name: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  children: ReactNode
  notion?: boolean
  values: DataProtectionFormValues
}

type DataProtectionFieldsetProps = {
  formProps: FormikProps<DataProtectionFormValues>
  tlsValid: boolean
  storefrontData: any
}

const CheckboxItem = ({
  label,
  name,
  onChange,
  children,
  notion,
  values,
}: CheckboxItemProps) => {
  return (
    <div className="o-layout__item u-1/1 u-1/1@xs">
      <div className="c-form-field">
        <div className="c-form-field__label">{label}</div>
        <div className="c-form-field__box">
          <FormField
            testId={name}
            type="select"
            name={name}
            render={() => (
              <Toggle
                name={name}
                id={name}
                inputType="checkbox"
                onOff
                onChange={onChange}
                checked={Boolean(get(values, name, false))}
              >
                {children}
                {notion && '*'}
              </Toggle>
            )}
          />
        </div>
      </div>
    </div>
  )
}

const DataProtectionFieldset = ({
  formProps,
  tlsValid,
  storefrontData,
}: DataProtectionFieldsetProps) => {
  const { t } = useTranslation()
  const { values, setFieldValue } = formProps

  const changeSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('allowPhoneAds', event.target.checked)
    setFieldValue('allowMailAds', event.target.checked)
  }

  const changeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked) {
      setFieldValue('selectall', event.target.checked)
    }
  }

  const changeMarketingConsent = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked
    setFieldValue('allowPhoneAds', isChecked)
    setFieldValue('allowMailAds', isChecked)
    setFieldValue('allowProfilesCreation', isChecked)
    setFieldValue('dataPrivacyConsentAcceptance', isChecked)
  }

  const CardItem = ({
    title,
    content,
  }: {
    title: ReactNode
    content?: ReactNode
  }) => <Card title={title} {...(content && { contentLarge: content , expandable: true })} />

const showPrivacyInformation = () => (
    <>
      <Heading level={4}>{t('form:dataProtection:title')}</Heading>
      <Fieldset.Row>
        <Paragraph
          dangerouslySetInnerHTML={{
            __html: t('form:dataProtection:Privacy:paragraph1'),
          }}
        />
      </Fieldset.Row>
    </>
  )

  const ConsentCheckbox = ({
    itemName = '',
    children,
  }: {
    itemName?: string
    children: ReactNode
  }) => (
    <Fieldset.Row>
      <Layout>
        <CheckboxItem name={itemName} onChange={changeCheckbox} values={values}>
          {children}
        </CheckboxItem>
      </Layout>
    </Fieldset.Row>
  )

  const showPrivacyConsents = () => (
    <Fieldset.Row>
      <CardItem
        title={
          <>
            <Fieldset.Row>
              <Heading level={4}>
                {t('form:dataProtection:Advertising:title')}
              </Heading>
              <Layout>
                <CheckboxItem
                  name="selectall"
                  onChange={changeSelectAll}
                  values={values}
                >
                  {t('form:dataProtection:Advertising:paragraph1')}
                </CheckboxItem>
              </Layout>
              <div style={{ display: 'none' }}>
                <ConsentCheckbox itemName="allowPhoneAds">
                  {t('form:dataProtection:mailAndTelAdsCheckTel')}
                </ConsentCheckbox>
                <ConsentCheckbox itemName="allowMailAds">
                  {t('form:dataProtection:mailAndTelAdsCheckMail')}
                </ConsentCheckbox>
              </div>
            </Fieldset.Row>
          </>
        }
        content={
          <>
            <Fieldset.Row>
              <p>{t('form:dataProtection:Advertising:paragraph2')}</p>
              <p
                dangerouslySetInnerHTML={{
                  __html: t('form:dataProtection:Advertising:paragraph3'),
                }}
              />
            </Fieldset.Row>
          </>
        }
      />
    </Fieldset.Row>
  )

  const showAllowCustomerProfiles = () => (
    <Fieldset.Row>
      <CardItem
        title={
          <>
            <Fieldset.Row>
              <Heading level={4}>
                {t('form:dataProtection:CustomerProfiles:title')}
              </Heading>
              <Layout>
                <CheckboxItem
                  name="allowProfilesCreation"
                  values={values}
                  onChange={() => null}
                >
                  {t('form:dataProtection:CustomerProfiles:paragraph1')}
                </CheckboxItem>
              </Layout>
            </Fieldset.Row>
          </>
        }
        content={
          <Fieldset.Row>
            <p>{t('form:dataProtection:CustomerProfiles:paragraph2')}</p>
            <p
              dangerouslySetInnerHTML={{
                __html: t('form:dataProtection:CustomerProfiles:paragraph3'),
              }}
            />
          </Fieldset.Row>
        }
      />
    </Fieldset.Row>
  )

  const showEmailCommunicationConsent = () => (
    <Fieldset.Row>
      <CardItem
        title={
          <>
            <Fieldset.Row>
              <Heading level={4}>{t('form:dataProtection:tls:title')}</Heading>

              <Layout>
                <CheckboxItem
                  name="dataPrivacyConsentAcceptance"
                  values={values}
                  onChange={() => null}
                >
                  <strong>{t('form:dataProtection:tls:text')}</strong>
                  {!values.dataPrivacyConsentAcceptance && (
                    <>
                      <br />
                      <span style={{ color: 'red' }}>
                        {t('form:dataProtection:tls:validation')}
                      </span>
                    </>
                  )}
                </CheckboxItem>
              </Layout>
            </Fieldset.Row>
          </>
        }
        content={
          <Fieldset.Row>
            <p>{t('form:dataProtection:Communication:paragraph1')}</p>
          </Fieldset.Row>
        }
      />
    </Fieldset.Row>
  )

  const showGeneralConditions = () => {
    return (
      <>
        <Fieldset.Row>
          <Heading level={4} id="GeneralConditionsTitle">
            {t('form:dataProtection:termsTitle')}
          </Heading>
          <FormField
            testId="termsAgreeCheckAgree"
            name="termsAgreeCheckAgree"
            type="checkbox"
            showErrorMessageFormField={false}
            render={(fieldProps: any) => (
              <Checkbox
                {...fieldProps}
                errorMessage={
                  fieldProps.error ? t('validation:required') : null
                }
                id={fieldProps.name}
                checked={Boolean(get(values, 'termsAgreeCheckAgree', false))}
                required
                autoComplete="off"
              >
                <Paragraph>
                  {t('form:dataProtection:termsAgree1')}
                  <a
                    href={t('form:dataProtection:AGBlink')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('form:dataProtection:termsAgree2')}
                  </a>
                  {t('form:dataProtection:termsAgree3')}
                  {t('form:dataProtection:termsAgree4', {
                    companyName: storefrontData?.companyData?.name,
                  })}
                </Paragraph>
              </Checkbox>
            )}
          />
        </Fieldset.Row>
      </>
    )
  }

  const MarketingConsentCheckboxContent = () => (
    <>
      <Paragraph>
        {t('form:dataProtection:MarketingConsent:paragraph1')}
      </Paragraph>
      <Paragraph>
        <a
          href={t(
            'form:dataProtection:MarketingConsent:dataPrivacyLink'
          )}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('form:dataProtection:MarketingConsent:paragraph2')}
        </a>
      </Paragraph>
        <Paragraph>
        {t('form:dataProtection:MarketingConsent:paragraph3')}
      </Paragraph>
      <Paragraph>
        <a
          href={`mailto:${t(
            'form:dataProtection:MarketingConsent:emailAddress1'
          )}`}
        >
          {t('form:dataProtection:MarketingConsent:emailAddress1')}
        </a>
        {t('form:dataProtection:MarketingConsent:paragraph4')}
        <a
          href={`mailto:${t(
            'form:dataProtection:MarketingConsent:emailAddress2'
          )}`}
        >
          {t('form:dataProtection:MarketingConsent:emailAddress2')}
        </a>
      </Paragraph>
    </>
  )

  const showMarketingConsent = () => (
    <Fieldset.Row>
      <Heading level={4}>
        {t('form:dataProtection:MarketingConsent:title')}
      </Heading>
      <Heading level={4}>
        {t('form:dataProtection:MarketingConsent:labelText')}
      </Heading>
      <FormField
        testId="dataPrivacyConsentAcceptance"
        name="dataPrivacyConsentAcceptance"
        type="checkbox"
        showErrorMessageFormField={false}
        render={(fieldProps: any) => (
          <Checkbox
            {...fieldProps}
            id={fieldProps.name}
            checked={Boolean(
              get(values, 'dataPrivacyConsentAcceptance', false)
            )}
            onChange={changeMarketingConsent}
            autoComplete="off"
          >
            <MarketingConsentCheckboxContent />
          </Checkbox>
        )}
      />
    </Fieldset.Row>
  )

  return (
    <Fieldset>
      {!isAudiOrVWFS() && showPrivacyInformation()}
      {!isAudiOrVWFS() && showPrivacyConsents()}
      {!isAudiOrVWFS() && showAllowCustomerProfiles()}
      {!isAudiOrVWFS() && tlsValid && showEmailCommunicationConsent()}
      {isAudiOrVWFS() && showMarketingConsent()}
      {showGeneralConditions()}
    </Fieldset>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    tlsValid: getTlsValid(state),
    storefrontData: getStorefrontData(state),
  }
}

export default connect(mapStateToProps)(DataProtectionFieldset)
